import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiArrowLeft, FiCheck, FiArrowRight, FiTarget, FiTrendingUp, FiBookOpen } = FiIcons;

const AssessmentDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [showResults, setShowResults] = useState(false);
  const [progress, setProgress] = useState(0);

  const assessmentData = {
    'leadership-style': {
      title: "Leadership Style Assessment",
      description: "Discover your natural leadership style and learn how to adapt it",
      questions: [
        {
          id: 1,
          question: "When faced with a team conflict, what is your first instinct?",
          options: [
            { value: "A", text: "Mediate immediately and help find common ground" },
            { value: "B", text: "Analyze the situation thoroughly before taking action" },
            { value: "C", text: "Set clear expectations and enforce team rules" },
            { value: "D", text: "Encourage the team to resolve it themselves" }
          ]
        },
        {
          id: 2,
          question: "How do you prefer to motivate your team?",
          options: [
            { value: "A", text: "Through personal connections and understanding their goals" },
            { value: "B", text: "By providing clear data and logical reasoning" },
            { value: "C", text: "With structured goals and recognition systems" },
            { value: "D", text: "By giving them autonomy and trusting their judgment" }
          ]
        },
        {
          id: 3,
          question: "When making important decisions, you typically:",
          options: [
            { value: "A", text: "Seek input from all stakeholders affected" },
            { value: "B", text: "Gather comprehensive data and analyze options" },
            { value: "C", text: "Make quick decisions based on experience" },
            { value: "D", text: "Delegate the decision to the most qualified person" }
          ]
        },
        {
          id: 4,
          question: "Your communication style is best described as:",
          options: [
            { value: "A", text: "Warm, empathetic, and relationship-focused" },
            { value: "B", text: "Detailed, logical, and fact-based" },
            { value: "C", text: "Direct, clear, and results-oriented" },
            { value: "D", text: "Flexible, adaptive, and situational" }
          ]
        },
        {
          id: 5,
          question: "When a team member is struggling with performance:",
          options: [
            { value: "A", text: "Have a heart-to-heart conversation to understand their challenges" },
            { value: "B", text: "Create a detailed improvement plan with metrics" },
            { value: "C", text: "Set clear expectations and timelines for improvement" },
            { value: "D", text: "Provide resources and let them find their own solution" }
          ]
        }
      ]
    },
    'emotional-intelligence': {
      title: "Emotional Intelligence Evaluation",
      description: "Assess your emotional awareness, regulation, and social skills",
      questions: [
        {
          id: 1,
          question: "How well do you recognize your emotional triggers?",
          options: [
            { value: "A", text: "I'm very aware of what triggers strong emotions in me" },
            { value: "B", text: "I usually notice my triggers after the fact" },
            { value: "C", text: "I sometimes recognize my triggers" },
            { value: "D", text: "I rarely think about what triggers my emotions" }
          ]
        },
        {
          id: 2,
          question: "When you're stressed, how do you typically respond?",
          options: [
            { value: "A", text: "I use specific techniques to manage my stress effectively" },
            { value: "B", text: "I try to push through and deal with it later" },
            { value: "C", text: "I sometimes lose my composure but recover quickly" },
            { value: "D", text: "I often feel overwhelmed and struggle to cope" }
          ]
        },
        {
          id: 3,
          question: "How easily can you read others' emotions?",
          options: [
            { value: "A", text: "I can usually tell how someone is feeling even if they don't say" },
            { value: "B", text: "I pick up on obvious emotional cues" },
            { value: "C", text: "I sometimes miss emotional signals from others" },
            { value: "D", text: "I focus more on what people say than how they feel" }
          ]
        },
        {
          id: 4,
          question: "When giving difficult feedback, you:",
          options: [
            { value: "A", text: "Consider the person's emotional state and choose your approach carefully" },
            { value: "B", text: "Focus on being direct and honest about the issues" },
            { value: "C", text: "Try to be gentle but sometimes avoid the hard conversations" },
            { value: "D", text: "Prefer to give feedback in writing or through others" }
          ]
        },
        {
          id: 5,
          question: "How do you handle disagreements with colleagues?",
          options: [
            { value: "A", text: "I listen to understand their perspective and find common ground" },
            { value: "B", text: "I present my case logically and expect others to see reason" },
            { value: "C", text: "I try to avoid conflict when possible" },
            { value: "D", text: "I stand firm on my position and expect others to adapt" }
          ]
        }
      ]
    },
    'communication-skills': {
      title: "Communication Skills Analyzer",
      description: "Evaluate your communication effectiveness and listening abilities",
      questions: [
        {
          id: 1,
          question: "During team meetings, how do you typically participate?",
          options: [
            { value: "A", text: "I actively facilitate discussion and ensure everyone has a voice" },
            { value: "B", text: "I prepare thoroughly and present my points clearly" },
            { value: "C", text: "I listen more than I speak and contribute when asked" },
            { value: "D", text: "I focus on keeping the meeting efficient and on-track" }
          ]
        },
        {
          id: 2,
          question: "When explaining complex ideas to your team, you:",
          options: [
            { value: "A", text: "Use analogies and stories to make concepts relatable" },
            { value: "B", text: "Break down information into logical, sequential steps" },
            { value: "C", text: "Check understanding frequently and adjust your approach" },
            { value: "D", text: "Provide comprehensive documentation and examples" }
          ]
        },
        {
          id: 3,
          question: "How do you handle interruptions during conversations?",
          options: [
            { value: "A", text: "I politely acknowledge the interruption and redirect back to the speaker" },
            { value: "B", text: "I address the interruption immediately if it's relevant" },
            { value: "C", text: "I let people finish their thoughts before responding" },
            { value: "D", text: "I use interruptions as opportunities to clarify or add information" }
          ]
        },
        {
          id: 4,
          question: "When receiving criticism or feedback, your first reaction is to:",
          options: [
            { value: "A", text: "Ask clarifying questions to fully understand the perspective" },
            { value: "B", text: "Listen carefully and take notes for later reflection" },
            { value: "C", text: "Thank the person and explain your reasoning" },
            { value: "D", text: "Defend your position with facts and data" }
          ]
        },
        {
          id: 5,
          question: "How do you adapt your communication style for different audiences?",
          options: [
            { value: "A", text: "I research my audience and tailor my message accordingly" },
            { value: "B", text: "I maintain consistency but adjust my examples and references" },
            { value: "C", text: "I read the room and adjust my tone and pace in real-time" },
            { value: "D", text: "I prepare multiple versions of my message for different groups" }
          ]
        }
      ]
    },
    'decision-making': {
      title: "Decision Making Assessment",
      description: "Analyze your decision-making process and problem-solving approach",
      questions: [
        {
          id: 1,
          question: "When facing a major decision with limited time, you:",
          options: [
            { value: "A", text: "Gather key stakeholders for a quick consultation" },
            { value: "B", text: "Rely on past experience and proven frameworks" },
            { value: "C", text: "Focus on the most critical factors and make the call" },
            { value: "D", text: "Delegate the decision to someone with relevant expertise" }
          ]
        },
        {
          id: 2,
          question: "How do you typically gather information for important decisions?",
          options: [
            { value: "A", text: "I seek diverse perspectives from multiple sources" },
            { value: "B", text: "I conduct thorough research and data analysis" },
            { value: "C", text: "I consult with trusted advisors and experts" },
            { value: "D", text: "I review similar past decisions and their outcomes" }
          ]
        },
        {
          id: 3,
          question: "When a decision you made doesn't work out as expected, you:",
          options: [
            { value: "A", text: "Analyze what went wrong and communicate lessons learned" },
            { value: "B", text: "Quickly pivot and implement a new solution" },
            { value: "C", text: "Take responsibility and work to minimize negative impact" },
            { value: "D", text: "Review the decision-making process for future improvement" }
          ]
        },
        {
          id: 4,
          question: "How do you handle decisions that affect multiple departments?",
          options: [
            { value: "A", text: "Facilitate collaborative decision-making sessions" },
            { value: "B", text: "Create a structured evaluation process with clear criteria" },
            { value: "C", text: "Make the decision and communicate the rationale clearly" },
            { value: "D", text: "Seek input individually then synthesize recommendations" }
          ]
        },
        {
          id: 5,
          question: "What's your approach to decisions with significant uncertainty?",
          options: [
            { value: "A", text: "Build consensus around the best available option" },
            { value: "B", text: "Create contingency plans for multiple scenarios" },
            { value: "C", text: "Make the decision and adjust course as new information emerges" },
            { value: "D", text: "Gather more information until uncertainty is reduced" }
          ]
        }
      ]
    },
    'team-management': {
      title: "Team Management Readiness",
      description: "Assess your readiness to manage and develop high-performing teams",
      questions: [
        {
          id: 1,
          question: "How do you approach setting goals for your team?",
          options: [
            { value: "A", text: "Collaborate with team members to set meaningful, achievable goals" },
            { value: "B", text: "Set clear, measurable objectives aligned with company strategy" },
            { value: "C", text: "Focus on stretch goals that challenge the team to excel" },
            { value: "D", text: "Allow team members to set their own goals within guidelines" }
          ]
        },
        {
          id: 2,
          question: "When delegating tasks, your primary consideration is:",
          options: [
            { value: "A", text: "Matching tasks to individual strengths and development needs" },
            { value: "B", text: "Ensuring the most qualified person handles each task" },
            { value: "C", text: "Distributing workload fairly across the team" },
            { value: "D", text: "Providing opportunities for skill development and growth" }
          ]
        },
        {
          id: 3,
          question: "How do you handle underperformance in your team?",
          options: [
            { value: "A", text: "Have private conversations to understand underlying issues" },
            { value: "B", text: "Create specific improvement plans with clear metrics" },
            { value: "C", text: "Address it quickly and directly with clear expectations" },
            { value: "D", text: "Provide additional training and mentoring support" }
          ]
        },
        {
          id: 4,
          question: "What's your approach to team meetings?",
          options: [
            { value: "A", text: "Focus on team building and open discussion" },
            { value: "B", text: "Follow structured agendas with clear outcomes" },
            { value: "C", text: "Keep them brief and action-oriented" },
            { value: "D", text: "Use them primarily for information sharing and updates" }
          ]
        },
        {
          id: 5,
          question: "How do you recognize and reward team achievements?",
          options: [
            { value: "A", text: "Celebrate both individual contributions and team success" },
            { value: "B", text: "Implement systematic recognition programs and metrics" },
            { value: "C", text: "Provide immediate acknowledgment and praise" },
            { value: "D", text: "Focus on providing growth opportunities as rewards" }
          ]
        }
      ]
    },
    'adaptive-leadership': {
      title: "Adaptive Leadership Capacity",
      description: "Measure your ability to lead through change and uncertainty",
      questions: [
        {
          id: 1,
          question: "When your organization faces unexpected changes, you:",
          options: [
            { value: "A", text: "Engage stakeholders to understand impact and build solutions together" },
            { value: "B", text: "Analyze the situation thoroughly before developing a response plan" },
            { value: "C", text: "Take decisive action to minimize disruption" },
            { value: "D", text: "Empower team members to adapt and respond autonomously" }
          ]
        },
        {
          id: 2,
          question: "How do you help your team navigate uncertainty?",
          options: [
            { value: "A", text: "Provide emotional support and maintain open communication" },
            { value: "B", text: "Share what information you have and explain the rationale for decisions" },
            { value: "C", text: "Focus on what can be controlled and maintain clear direction" },
            { value: "D", text: "Encourage experimentation and learning from failures" }
          ]
        },
        {
          id: 3,
          question: "When facing resistance to necessary changes, you:",
          options: [
            { value: "A", text: "Listen to concerns and work to address underlying fears" },
            { value: "B", text: "Present compelling evidence for why change is necessary" },
            { value: "C", text: "Clearly communicate expectations and consequences" },
            { value: "D", text: "Find early adopters to model the desired changes" }
          ]
        },
        {
          id: 4,
          question: "Your approach to innovation and new ideas is to:",
          options: [
            { value: "A", text: "Create safe spaces for creative thinking and experimentation" },
            { value: "B", text: "Evaluate ideas systematically against strategic objectives" },
            { value: "C", text: "Implement promising ideas quickly with clear success metrics" },
            { value: "D", text: "Encourage team members to pilot new approaches independently" }
          ]
        },
        {
          id: 5,
          question: "How do you maintain team morale during difficult transitions?",
          options: [
            { value: "A", text: "Focus on team cohesion and mutual support" },
            { value: "B", text: "Communicate progress regularly and celebrate small wins" },
            { value: "C", text: "Maintain confidence and project stability" },
            { value: "D", text: "Provide learning opportunities and skill development" }
          ]
        }
      ]
    }
  };

  const assessment = assessmentData[id];

  useEffect(() => {
    if (!assessment) {
      navigate('/assessments');
    }
  }, [assessment, navigate]);

  useEffect(() => {
    const newProgress = ((currentQuestion + 1) / assessment?.questions.length) * 100;
    setProgress(newProgress);
  }, [currentQuestion, assessment]);

  if (!assessment) {
    return <div>Assessment not found</div>;
  }

  const handleAnswer = (value) => {
    setAnswers({ ...answers, [currentQuestion]: value });
  };

  const nextQuestion = () => {
    if (currentQuestion < assessment.questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    } else {
      setShowResults(true);
    }
  };

  const prevQuestion = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
    }
  };

  const calculateResults = () => {
    const answerCounts = { A: 0, B: 0, C: 0, D: 0 };
    Object.values(answers).forEach(answer => {
      answerCounts[answer]++;
    });

    const maxCount = Math.max(...Object.values(answerCounts));
    const dominantStyle = Object.keys(answerCounts).find(key => answerCounts[key] === maxCount);

    const resultsByAssessment = {
      'leadership-style': {
        A: {
          name: "Collaborative Leader",
          description: "You lead through relationships and consensus-building.",
          strengths: ["Excellent interpersonal skills", "Builds strong team relationships", "Creates inclusive environments"],
          development: ["Practice making tough decisions independently", "Work on setting clearer boundaries", "Develop more directive communication when needed"]
        },
        B: {
          name: "Analytical Leader", 
          description: "You lead through data, logic, and systematic thinking.",
          strengths: ["Strong problem-solving abilities", "Makes well-informed decisions", "Excellent at strategic planning"],
          development: ["Improve emotional intelligence", "Practice more flexible decision-making", "Work on inspiring and motivating others"]
        },
        C: {
          name: "Directive Leader",
          description: "You lead through clear direction and decisive action.",
          strengths: ["Excellent in crisis situations", "Provides clear direction", "Drives results effectively"],
          development: ["Practice more collaborative approaches", "Improve listening skills", "Work on empathy and emotional awareness"]
        },
        D: {
          name: "Adaptive Leader",
          description: "You lead through flexibility and empowerment.",
          strengths: ["Highly flexible and adaptable", "Empowers team members", "Excellent at developing others"],
          development: ["Practice more decisive leadership when needed", "Work on providing clearer direction", "Improve accountability systems"]
        }
      },
      'emotional-intelligence': {
        A: {
          name: "Emotionally Intelligent Leader",
          description: "You have strong emotional awareness and regulation skills.",
          strengths: ["High self-awareness", "Excellent empathy skills", "Strong emotional regulation"],
          development: ["Continue practicing advanced EI techniques", "Help others develop emotional intelligence", "Apply EI skills in complex situations"]
        },
        B: {
          name: "Developing Emotional Intelligence",
          description: "You have good emotional awareness with room for growth.",
          strengths: ["Growing self-awareness", "Logical approach to emotions", "Consistent in relationships"],
          development: ["Practice emotional regulation techniques", "Improve empathy and social awareness", "Work on reading non-verbal cues"]
        },
        C: {
          name: "Emerging Emotional Intelligence",
          description: "You're building your emotional intelligence foundation.",
          strengths: ["Willing to learn and grow", "Good analytical skills", "Stable under pressure"],
          development: ["Focus on self-awareness exercises", "Practice active listening", "Develop emotional vocabulary"]
        },
        D: {
          name: "Early Stage Emotional Intelligence",
          description: "You have significant opportunities to develop emotional intelligence.",
          strengths: ["Task-focused approach", "Consistent performance", "Direct communication"],
          development: ["Start with basic emotional awareness", "Practice mindfulness techniques", "Seek feedback on interpersonal skills"]
        }
      },
      'communication-skills': {
        A: {
          name: "Engaging Communicator",
          description: "You excel at interactive and inclusive communication.",
          strengths: ["Excellent facilitation skills", "Creates inclusive environments", "Strong storytelling ability"],
          development: ["Practice concise communication", "Work on written communication", "Develop presentation skills for larger audiences"]
        },
        B: {
          name: "Structured Communicator",
          description: "You communicate with clarity and logical organization.",
          strengths: ["Clear and organized", "Excellent preparation skills", "Strong analytical communication"],
          development: ["Add more emotional connection", "Practice spontaneous communication", "Improve non-verbal communication"]
        },
        C: {
          name: "Thoughtful Communicator",
          description: "You are a careful listener who communicates with consideration.",
          strengths: ["Excellent listening skills", "Thoughtful responses", "Adaptable communication style"],
          development: ["Build confidence in speaking up", "Practice assertive communication", "Develop leadership presence"]
        },
        D: {
          name: "Efficient Communicator",
          description: "You focus on clear, efficient, and results-oriented communication.",
          strengths: ["Clear and direct", "Results-focused", "Good documentation skills"],
          development: ["Improve interpersonal connection", "Practice active listening", "Work on emotional expression"]
        }
      },
      'decision-making': {
        A: {
          name: "Collaborative Decision Maker",
          description: "You excel at inclusive and participatory decision-making.",
          strengths: ["Strong stakeholder engagement", "Builds buy-in and consensus", "Considers multiple perspectives"],
          development: ["Practice making faster decisions when needed", "Work on decisive leadership", "Balance inclusion with efficiency"]
        },
        B: {
          name: "Analytical Decision Maker",
          description: "You make decisions based on thorough analysis and data.",
          strengths: ["Excellent research skills", "Data-driven approach", "Systematic evaluation process"],
          development: ["Practice intuitive decision-making", "Speed up decision process", "Include more stakeholder input"]
        },
        C: {
          name: "Decisive Leader",
          description: "You make quick, confident decisions and take responsibility.",
          strengths: ["Quick decision-making", "Strong accountability", "Effective in crisis situations"],
          development: ["Gather more input before deciding", "Consider long-term implications", "Improve consultation skills"]
        },
        D: {
          name: "Strategic Decision Maker",
          description: "You focus on learning and continuous improvement in decisions.",
          strengths: ["Learning-oriented approach", "Good at delegation", "Strong pattern recognition"],
          development: ["Take more ownership of decisions", "Practice real-time decision-making", "Build confidence in judgment"]
        }
      },
      'team-management': {
        A: {
          name: "People-Centered Manager",
          description: "You excel at developing individuals and building team cohesion.",
          strengths: ["Strong team building skills", "Excellent at individual development", "Creates positive team culture"],
          development: ["Balance people focus with results", "Improve performance management", "Develop more structure in processes"]
        },
        B: {
          name: "Systems-Oriented Manager",
          description: "You manage through clear processes and structured approaches.",
          strengths: ["Excellent planning skills", "Clear performance metrics", "Strong organizational abilities"],
          development: ["Improve interpersonal connections", "Practice flexible management", "Focus more on individual needs"]
        },
        C: {
          name: "Results-Driven Manager",
          description: "You focus on achieving results and maintaining high standards.",
          strengths: ["Strong performance focus", "Clear expectations", "Effective at driving results"],
          development: ["Improve team development focus", "Practice more collaborative approaches", "Work on emotional intelligence"]
        },
        D: {
          name: "Development-Focused Manager",
          description: "You prioritize growth and empowerment of team members.",
          strengths: ["Excellent coaching skills", "Strong at delegation", "Focuses on long-term development"],
          development: ["Balance development with performance", "Improve accountability systems", "Practice more directive management when needed"]
        }
      },
      'adaptive-leadership': {
        A: {
          name: "Collaborative Change Leader",
          description: "You lead change through engagement and emotional intelligence.",
          strengths: ["Excellent stakeholder engagement", "Strong emotional support", "Builds change resilience"],
          development: ["Practice more decisive change leadership", "Speed up change implementation", "Balance support with urgency"]
        },
        B: {
          name: "Strategic Change Leader",
          description: "You lead change through analysis and systematic planning.",
          strengths: ["Excellent change planning", "Data-driven approach", "Clear communication of rationale"],
          development: ["Improve emotional aspects of change", "Practice more flexible approaches", "Speed up implementation"]
        },
        C: {
          name: "Decisive Change Leader",
          description: "You lead change through clear direction and quick action.",
          strengths: ["Quick implementation", "Clear direction", "Maintains stability during change"],
          development: ["Improve stakeholder engagement", "Work on change communication", "Consider emotional impacts"]
        },
        D: {
          name: "Empowering Change Leader",
          description: "You lead change by empowering others and fostering innovation.",
          strengths: ["Encourages innovation", "Empowers team adaptation", "Focuses on learning"],
          development: ["Provide more structure during change", "Improve change coordination", "Balance autonomy with guidance"]
        }
      }
    };

    return resultsByAssessment[id][dominantStyle];
  };

  if (showResults) {
    const results = calculateResults();
    return (
      <div className="min-h-screen py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-12"
          >
            <div className="w-20 h-20 bg-gradient-to-r from-green-500 to-green-600 rounded-full flex items-center justify-center mx-auto mb-6">
              <SafeIcon icon={FiCheck} className="text-white text-3xl" />
            </div>
            <h1 className="text-4xl font-bold text-gray-900 mb-4">Assessment Complete!</h1>
            <p className="text-xl text-gray-600">Here are your personalized results</p>
          </motion.div>

          <div className="bg-white rounded-2xl shadow-lg p-8 mb-8">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-2">Your Result</h2>
              <h3 className="text-2xl font-semibold text-blue-600 mb-4">{results.name}</h3>
              <p className="text-lg text-gray-600">{results.description}</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="bg-green-50 rounded-xl p-6">
                <div className="flex items-center mb-4">
                  <SafeIcon icon={FiTarget} className="text-green-600 text-xl mr-2" />
                  <h4 className="text-xl font-bold text-gray-900">Your Strengths</h4>
                </div>
                <ul className="space-y-2">
                  {results.strengths.map((strength, index) => (
                    <li key={index} className="flex items-start">
                      <div className="w-2 h-2 bg-green-500 rounded-full mt-2 mr-3"></div>
                      <span className="text-gray-700">{strength}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="bg-blue-50 rounded-xl p-6">
                <div className="flex items-center mb-4">
                  <SafeIcon icon={FiTrendingUp} className="text-blue-600 text-xl mr-2" />
                  <h4 className="text-xl font-bold text-gray-900">Development Areas</h4>
                </div>
                <ul className="space-y-2">
                  {results.development.map((area, index) => (
                    <li key={index} className="flex items-start">
                      <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 mr-3"></div>
                      <span className="text-gray-700">{area}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => navigate('/assessments')}
              className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-8 py-3 rounded-xl font-semibold hover:shadow-lg transition-all duration-300 flex items-center justify-center space-x-2"
            >
              <span>Take Another Assessment</span>
              <SafeIcon icon={FiBookOpen} className="text-lg" />
            </button>
            <button
              onClick={() => navigate('/leadership')}
              className="border-2 border-blue-600 text-blue-600 px-8 py-3 rounded-xl font-semibold hover:bg-blue-50 transition-all duration-300"
            >
              Explore Leadership Topics
            </button>
          </div>
        </div>
      </div>
    );
  }

  const currentQ = assessment.questions[currentQuestion];

  return (
    <div className="min-h-screen py-20">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <button
            onClick={() => navigate('/assessments')}
            className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 transition-colors duration-200 mb-6"
          >
            <SafeIcon icon={FiArrowLeft} className="text-lg" />
            <span>Back to Assessments</span>
          </button>

          <div className="bg-white rounded-2xl shadow-lg p-8">
            <div className="mb-8">
              <div className="flex justify-between items-center mb-4">
                <h1 className="text-2xl font-bold text-gray-900">{assessment.title}</h1>
                <span className="text-sm text-gray-500">
                  Question {currentQuestion + 1} of {assessment.questions.length}
                </span>
              </div>

              <div className="w-full bg-gray-200 rounded-full h-2 mb-6">
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: `${progress}%` }}
                  transition={{ duration: 0.5 }}
                  className="bg-gradient-to-r from-blue-600 to-purple-600 h-2 rounded-full"
                ></motion.div>
              </div>
            </div>

            <motion.div
              key={currentQuestion}
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
            >
              <h2 className="text-xl font-semibold text-gray-900 mb-6">
                {currentQ.question}
              </h2>

              <div className="space-y-4">
                {currentQ.options.map((option) => (
                  <label
                    key={option.value}
                    className={`block p-4 rounded-xl border-2 cursor-pointer transition-all duration-200 ${
                      answers[currentQuestion] === option.value
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    <input
                      type="radio"
                      name={`question-${currentQuestion}`}
                      value={option.value}
                      checked={answers[currentQuestion] === option.value}
                      onChange={() => handleAnswer(option.value)}
                      className="sr-only"
                    />
                    <span className="text-gray-700">{option.text}</span>
                  </label>
                ))}
              </div>
            </motion.div>

            <div className="flex justify-between mt-8">
              <button
                onClick={prevQuestion}
                disabled={currentQuestion === 0}
                className={`px-6 py-3 rounded-xl font-semibold transition-all duration-300 ${
                  currentQuestion === 0
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                Previous
              </button>

              <button
                onClick={nextQuestion}
                disabled={!answers[currentQuestion]}
                className={`px-6 py-3 rounded-xl font-semibold transition-all duration-300 flex items-center space-x-2 ${
                  !answers[currentQuestion]
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-gradient-to-r from-blue-600 to-purple-600 text-white hover:shadow-lg'
                }`}
              >
                <span>{currentQuestion === assessment.questions.length - 1 ? 'Finish' : 'Next'}</span>
                <SafeIcon icon={FiArrowRight} className="text-lg" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AssessmentDetail;